import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

export interface ContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: 'malware_analysis' | 'vuln_mining' | 'reverse-model' | 'fine-tuning' | 'gpu-cloud' | 'llm-platform';
}

export default function ContactFormModal({ isOpen, onClose, product }: ContactFormModalProps) {
  const { t } = useTranslation('products');
  const nameRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [serverError, setServerError] = useState('');

  // Focus name field on open
  useEffect(() => {
    if (isOpen) {
      setStatus('idle');
      setErrors({});
      setServerError('');
      setTimeout(() => nameRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Escape key closes modal
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Auto-close after success
  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => {
        onClose();
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
        setStatus('idle');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [status, onClose]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = t('contact_form.validation_required');
    if (!formData.email.trim()) {
      newErrors.email = t('contact_form.validation_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact_form.validation_email');
    }
    if (!formData.phone.trim()) newErrors.phone = t('contact_form.validation_required');
    if (!formData.company.trim()) newErrors.company = t('contact_form.validation_required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('submitting');
    setServerError('');

    try {
      await axios.post(`${import.meta.env.VITE_API_URL}/api/contact-requests`, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        company: formData.company.trim(),
        product,
        message: formData.message.trim() || undefined,
      });
      setStatus('success');
    } catch (err: any) {
      setStatus('error');
      setServerError(err?.response?.data?.error || t('contact_form.error'));
    }
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  if (!isOpen) return null;

  const inputClass = (field: string) =>
    `w-full px-3 py-2 bg-background border ${errors[field] ? 'border-destructive' : 'border-input'} rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all`;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border/50 rounded-xl shadow-macos-xl max-w-md w-full mx-4 animate-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground tracking-tight">{t('contact_form.title')}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">{t('contact_form.subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            aria-label={t('common:close')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {status === 'success' ? (
          /* Success state */
          <div className="px-5 pb-6 pt-4 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mb-3">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
            </div>
            <h3 className="text-base font-semibold text-foreground mb-1">{t('contact_form.success_title')}</h3>
            <p className="text-sm text-muted-foreground">{t('contact_form.success_desc')}</p>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="px-5 pb-5">
            <div className="space-y-3">
              {/* Name */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">{t('contact_form.name')}</label>
                <input
                  ref={nameRef}
                  type="text"
                  value={formData.name}
                  onChange={handleChange('name')}
                  placeholder={t('contact_form.name_placeholder')}
                  className={inputClass('name')}
                />
                {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">{t('contact_form.email')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  placeholder={t('contact_form.email_placeholder')}
                  className={inputClass('email')}
                />
                {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">{t('contact_form.phone')}</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange('phone')}
                  placeholder={t('contact_form.phone_placeholder')}
                  className={inputClass('phone')}
                />
                {errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}
              </div>

              {/* Company */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">{t('contact_form.company')}</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={handleChange('company')}
                  placeholder={t('contact_form.company_placeholder')}
                  className={inputClass('company')}
                />
                {errors.company && <p className="text-xs text-destructive mt-1">{errors.company}</p>}
              </div>

              {/* Message */}
              <div>
                <label className="block text-[13px] font-medium text-foreground mb-1">{t('contact_form.message')}</label>
                <textarea
                  value={formData.message}
                  onChange={handleChange('message')}
                  placeholder={t('contact_form.message_placeholder')}
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all resize-none"
                />
              </div>
            </div>

            {/* Server error */}
            {serverError && (
              <p className="text-sm text-destructive mt-3">{serverError}</p>
            )}

            {/* Actions */}
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-foreground bg-muted/50 hover:bg-muted rounded-lg transition-all active:scale-[0.98]"
              >
                {t('contact_form.cancel')}
              </button>
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="flex-1 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:brightness-110 disabled:opacity-50 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
              >
                {status === 'submitting' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('contact_form.submitting')}
                  </>
                ) : (
                  t('contact_form.submit')
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
